import { i18n } from '@secret-escapes/i18n';

import { ITranslationObject } from '../../common/types';
import {
  HOURS_IN_DAY,
  MILLISECONDS_IN_SECOND,
  MINUTES_IN_HOUR,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
} from '../../common/types/constants';

export { getSecondsLeft, getShortCountdownString, secondsToTime, getFullCountdownString };

export interface ITime {
  hours: number;
  minutes: number;
  seconds: number;
  days: number;
}

const timeThresholdDays: number = 7;

/**
 * Calculates the number of seconds left based on current date and time and the endDate property.
 */
const getSecondsLeft: (endDate: Date) => number = endDate => {
  const now: number = Date.now();
  const secondsLeft: number = Math.ceil((endDate.getTime() - now) / MILLISECONDS_IN_SECOND);

  return secondsLeft;
};

/**
 * Converts a number representing the seconds to a structure with number of hours, minutes and seconds left on the timer.
 * @param secs The number of remaining seconds on the count down timer.
 */
const secondsToTime: (secs: number) => ITime = secs => {
  const hours: number = Math.floor(secs / SECONDS_IN_HOUR) % HOURS_IN_DAY;
  const days: number = Math.floor(secs / (SECONDS_IN_HOUR * HOURS_IN_DAY));

  const divisorForMinutes: number = secs % SECONDS_IN_HOUR;
  const minutes: number = Math.floor(divisorForMinutes / MINUTES_IN_HOUR);

  const divisorForSeconds: number = divisorForMinutes % SECONDS_IN_MINUTE;
  const secondsInTime: number = Math.ceil(divisorForSeconds);

  return {
    days,
    hours,
    minutes,
    seconds: secondsInTime,
  };
};

/**
 * Get time remaining in the form Xh Xmin Xsec
 */
const getFullCountdownString: (time: ITime, translations: ITranslationObject) => string = (
  time,
  translations
) => {
  const aboutToEnd: string = i18n('sale.endsIn.today', [], translations);
  const secondsTranslation: string = i18n('urgency.countdown.seconds', [], translations);
  const minutesTranslation: string = i18n('urgency.countdown.minutes', [], translations);
  const hoursTranslation: string = i18n('urgency.countdown.hours', [], translations);

  if (time.days > 0) {
    if (time.days >= timeThresholdDays) {
      return i18n('sale.endsIn.limited.time', [], translations);
    } else {
      return i18n('sale.endsIn.days', [time.days], translations);
    }
  } else if (time.hours > 0) {
    return `${aboutToEnd} ${time.hours}${hoursTranslation} ${time.minutes}${minutesTranslation} ${time.seconds}${secondsTranslation}`;
  } else if (time.minutes > 0 || time.seconds > 0) {
    return `${aboutToEnd} ${time.minutes}${minutesTranslation} ${time.seconds}${secondsTranslation}`;
  } else {
    return aboutToEnd;
  }
};

/**
 * Get time remaining in the form Xh or Xmin (if under an hour)
 */
const getShortCountdownString: (time: ITime, translations: ITranslationObject) => string = (
  time,
  translations
) => {
  if (time.days > 0) {
    if (time.days >= timeThresholdDays) {
      return i18n('sale.endsIn.limited.time', [], translations);
    } else {
      return i18n('sale.endsIn.days', [time.days], translations);
    }
  } else if (time.hours > 0) {
    return i18n('sale.endsIn.hours', [time.hours], translations);
  } else {
    return i18n('sale.endsIn.today', [], translations);
  }
};
